import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import '../../assets/vendor/bootstrap/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import { database, ref, onValue, update, auth, googleProvider, signInWithPopup, signOut, onAuthStateChanged, storage, refStorage, uploadBytesResumable, getDownloadURL } from '../../services/firebase_connection';

const AUTHORIZED_EMAIL = "amontiel.developer@gmail.com";

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState('');

  const [activeTab, setActiveTab] = useState('es'); // 'config', 'es', 'us', 'messages', 'visits'
  const [masterData, setMasterData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [visitsList, setVisitsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Verificación de Sesión
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        if (currentUser.email === AUTHORIZED_EMAIL) {
          setUser(currentUser);
          setAuthError('');
        } else {
          // Usuario no autorizado
          signOut(auth);
          setUser(null);
          setAuthError(`El correo ${currentUser.email} no tiene permisos para acceder.`);
        }
      } else {
        setUser(null);
      }
      setAuthChecking(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = () => {
    setAuthError('');
    signInWithPopup(auth, googleProvider)
      .catch((error) => {
        setAuthError('Error al iniciar sesión: ' + error.message);
      });
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      setUser(null);
    });
  };

  useEffect(() => {
    if (!user) return; // Solo carga datos si está autorizado

    const dataRef = ref(database, 'master-data');
    const unsubscribeData = onValue(dataRef, (snapshot) => {
      if (snapshot.exists()) {
        setMasterData(snapshot.val());
      }
      setLoading(false);
    });

    const msgRef = ref(database, 'messages');
    const unsubscribeMsg = onValue(msgRef, (snapshot) => {
      if (snapshot.exists()) {
        const msgsData = snapshot.val();
        let allMessages = [];
        Object.keys(msgsData).forEach(lang => {
           const langMessages = msgsData[lang];
           Object.keys(langMessages).forEach(msgId => {
             allMessages.push({ id: msgId, lang: lang, ...langMessages[msgId] });
           });
        });
        allMessages.sort((a, b) => new Date(b.date) - new Date(a.date));
        setMessages(allMessages);
      } else {
        setMessages([]);
      }
    });

    const parseDateStr = (dateStr) => {
      if (!dateStr) return 0;
      const parts = dateStr.split('-');
      if (parts.length < 8) return 0;
      let hh = parseInt(parts[4]);
      if (parts[7] === 'PM' && hh !== 12) hh += 12;
      if (parts[7] === 'AM' && hh === 12) hh = 0;
      return new Date(
        parseInt(parts[2]), 
        parseInt(parts[1]) - 1, 
        parseInt(parts[0]), 
        hh, 
        parseInt(parts[5]), 
        parseInt(parts[6])
      ).getTime();
    };

    const visitsRef = ref(database, 'visits');
    const unsubscribeVisits = onValue(visitsRef, (snapshot) => {
      if (snapshot.exists()) {
        const vData = snapshot.val();
        const vArray = [];
        Object.keys(vData).forEach(key => {
          if (key !== 'count' && vData[key].register) {
            vArray.push({ id: key, ...vData[key] });
          }
        });
        vArray.sort((a, b) => parseDateStr(b.register) - parseDateStr(a.register));
        setVisitsList(vArray.slice(0, 5));
      } else {
        setVisitsList([]);
      }
    });

    return () => {
      unsubscribeData();
      unsubscribeMsg();
      unsubscribeVisits();
    };
  }, [user]);

  const handleInputChange = (path, value) => {
    setMasterData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      let current = newData;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) current[path[i]] = {}; // Create if doesn't exist
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      return newData;
    });
  };

  const handleArrayAdd = (path, newItem) => {
    setMasterData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      let current = newData;
      for (let i = 0; i < path.length; i++) {
        if (!current[path[i]]) current[path[i]] = [];
        current = current[path[i]];
      }
      if (Array.isArray(current)) {
        current.push(newItem);
      }
      return newData;
    });
  };

  const handleArrayRemove = (path, index) => {
    setMasterData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      let current = newData;
      for (let i = 0; i < path.length; i++) {
        if (!current[path[i]]) return newData;
        current = current[path[i]];
      }
      if (Array.isArray(current)) {
        current.splice(index, 1);
      }
      return newData;
    });
  };

  const handleSave = () => {
    setSaving(true);
    const dataRef = ref(database, 'master-data');
    update(dataRef, masterData)
      .then(() => {
        showToast('Cambios guardados correctamente.');
        setSaving(false);
      })
      .catch((error) => {
        showToast('Error al guardar: ' + error.message);
        setSaving(false);
      });
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleFileUpload = (e, pathArray, folderName) => {
    const file = e.target.files[0];
    if (!file) return;

    showToast(`Subiendo archivo a ${folderName}...`);

    const fileRef = refStorage(storage, `${folderName}/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on('state_changed', 
      (snapshot) => {
        // Podríamos manejar progreso aquí si quisiéramos
      }, 
      (error) => {
        showToast('Error al subir archivo: ' + error.message);
      }, 
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          handleInputChange(pathArray, downloadURL);
          showToast('Archivo subido con éxito y URL enlazada.');
        });
      }
    );
  };

  // ------------------ RENDER HELPERS ------------------ //

  const renderInput = (label, path, isTextArea = false, type="text") => {
    let value = masterData;
    for (let key of path) {
      if (value) value = value[key];
    }
    value = value || '';

    return (
      <div className="form-group mb-3">
        <label className="fw-semibold">{label}</label>
        {isTextArea ? (
          <textarea className="form-control" rows="4" value={value} onChange={(e) => handleInputChange(path, e.target.value)} />
        ) : (
          <input type={type} className="form-control" value={value} onChange={(e) => handleInputChange(path, e.target.value)} />
        )}
      </div>
    );
  };

  const renderFileInput = (label, path, folderName, placeholder="URL o subir archivo...") => {
    let value = masterData;
    for (let key of path) {
      if (value) value = value[key];
    }
    value = value || '';
    const fileInputId = `file_${path.join('_')}`;

    return (
      <div className="form-group mb-3">
        <label className="fw-semibold">{label}</label>
        <div className="input-group">
          <input type="text" className="form-control" placeholder={placeholder} value={value} onChange={(e) => handleInputChange(path, e.target.value)} />
          <input type="file" id={fileInputId} className="d-none" onChange={(e) => { handleFileUpload(e, path, folderName); e.target.value = ''; }} />
          <button className="btn btn-outline-primary" type="button" onClick={() => document.getElementById(fileInputId).click()}>
            <i className="bi bi-upload"></i> Subir
          </button>
        </div>
      </div>
    );
  };


  const renderConfigSection = () => {
    if (!masterData || !masterData.config) return null;
    return (
      <div className="admin-card">
        <h5>Imágenes y CV</h5>
        {renderFileInput('URL Imagen de Perfil', ['config', 'profileImage'], 'profile')}
        {renderFileInput('URL Imagen de Fondo (Hero)', ['config', 'profileBackgroud'], 'profile')}
        {renderFileInput('URL de CV', ['config', 'cvPath'], 'cv')}
        {renderInput('Texto botón descarga CV', ['config', 'labelDownload'])}

        <hr className="my-4" />
        <h5>Redes Sociales</h5>
        {(masterData.config.socialLinks || []).map((link, idx) => (
          <div key={idx} className="card p-3 mb-3 bg-light border-0 shadow-sm">
            <div className="d-flex justify-content-between mb-2">
              <h6 className="m-0">Enlace {idx + 1}</h6>
              <button className="btn btn-sm btn-danger" onClick={() => handleArrayRemove(['config', 'socialLinks'], idx)}>
                <i className="bi bi-trash"></i> Eliminar
              </button>
            </div>
            <div className="row">
              <div className="col-md-6">{renderInput('Clase (ej. facebook)', ['config', 'socialLinks', idx, 'className'])}</div>
              <div className="col-md-6">{renderInput('Icono (ej. bi bi-facebook)', ['config', 'socialLinks', idx, 'iconClass'])}</div>
              <div className="col-md-12">{renderInput('URL (href)', ['config', 'socialLinks', idx, 'href'])}</div>
            </div>
          </div>
        ))}
        <button className="btn btn-outline-primary btn-sm" onClick={() => handleArrayAdd(['config', 'socialLinks'], { className: '', iconClass: '', href: '', download: false })}>
          <i className="bi bi-plus-circle"></i> Agregar Red Social
        </button>
      </div>
    );
  };

  const renderLanguageSection = (lang) => {
    if (!masterData || !masterData[lang]) return null;
    const lPath = [lang];

    return (
      <div className="accordion" id={`accordion-${lang}`}>
        
        {/* PROFILE */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseProfile-${lang}`}>
              Perfil Principal (Profile)
            </button>
          </h2>
          <div id={`collapseProfile-${lang}`} className="accordion-collapse collapse show" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              <div className="row">
                <div className="col-md-6">{renderInput('Nombre', [...lPath, 'profile', 'name'])}</div>
                <div className="col-md-6">{renderInput('Apellido', [...lPath, 'profile', 'surname'])}</div>
                <div className="col-md-6">{renderInput('Nombre Completo', [...lPath, 'profile', 'fullname'])}</div>
                <div className="col-md-6">{renderInput('Correo', [...lPath, 'profile', 'email'])}</div>
                <div className="col-md-6">{renderInput('Teléfono', [...lPath, 'profile', 'phone'])}</div>
                <div className="col-md-6">{renderInput('Ciudad', [...lPath, 'profile', 'city'])}</div>
                <div className="col-md-6">{renderInput('Cumpleaños', [...lPath, 'profile', 'birthday'])}</div>
                <div className="col-md-6">{renderInput('Grado', [...lPath, 'profile', 'degree'])}</div>
                <div className="col-md-6">{renderInput('Sitio Web', [...lPath, 'profile', 'website'])}</div>
                <div className="col-md-6">{renderInput('Disponibilidad', [...lPath, 'profile', 'freelance'])}</div>
              </div>
            </div>
          </div>
        </div>

        {/* HERO / WELCOME DATA */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseHero-${lang}`}>
              Sección: Hero (Inicio)
            </button>
          </h2>
          <div id={`collapseHero-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              {renderInput('Título', [...lPath, 'profile', 'welcomeData', 'title'])}
              {renderInput('Texto Inicial (Soy...)', [...lPath, 'profile', 'welcomeData', 'textStart'])}
              {renderInput('Frase', [...lPath, 'profile', 'welcomeData', 'phrase'])}
              {renderInput('Descripción Corta', [...lPath, 'profile', 'welcomeData', 'sortDescription'], true)}
              
              <hr />
              <h6>Textos Animados (Máquina de escribir)</h6>
              {(masterData[lang].profile?.welcomeData?.textDescription || []).map((t, idx) => (
                <div key={idx} className="d-flex mb-2 gap-2">
                  <input type="text" className="form-control" value={t.value} onChange={(e) => handleInputChange([...lPath, 'profile', 'welcomeData', 'textDescription', idx, 'value'], e.target.value)} />
                  <button className="btn btn-danger" onClick={() => handleArrayRemove([...lPath, 'profile', 'welcomeData', 'textDescription'], idx)}><i className="bi bi-trash"></i></button>
                </div>
              ))}
              <button className="btn btn-outline-primary btn-sm mt-2" onClick={() => handleArrayAdd([...lPath, 'profile', 'welcomeData', 'textDescription'], { value: '' })}>+ Agregar Texto Animado</button>
            </div>
          </div>
        </div>

        {/* ABOUT */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseAbout-${lang}`}>
              Sección: Acerca de Mí (About)
            </button>
          </h2>
          <div id={`collapseAbout-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              {renderInput('Título de la Sección', [...lPath, 'sections', 'about', 'sectionTitle'])}
              {renderInput('Título Interno', [...lPath, 'sections', 'about', 'title'])}
              {renderInput('Frase', [...lPath, 'sections', 'about', 'phrase'])}
              {renderInput('Descripción Larga', [...lPath, 'sections', 'about', 'description'], true)}
              {renderInput('Título del Rol', [...lPath, 'sections', 'about', 'titleRol'])}
              {renderInput('Descripción del Rol', [...lPath, 'sections', 'about', 'descriptionRol'], true)}
            </div>
          </div>
        </div>

        {/* RESUME */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseResume-${lang}`}>
              Sección: Resumen (Resume)
            </button>
          </h2>
          <div id={`collapseResume-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              {renderInput('Título de la Sección', [...lPath, 'sections', 'resume', 'sectionTitle'])}
              {renderInput('Descripción de la Sección', [...lPath, 'sections', 'resume', 'sectionDescription'], true)}

              <hr className="my-4"/>
              <h5>Educación</h5>
              {renderInput('Título (Educación)', [...lPath, 'sections', 'resume', 'education', 'title'])}
              {(masterData[lang].sections?.resume?.education?.registers || []).map((edu, idx) => (
                <div key={idx} className="card p-3 mb-3 bg-light border-0 shadow-sm">
                  <div className="d-flex justify-content-between mb-2">
                    <h6 className="m-0">Educación {idx + 1}</h6>
                    <button className="btn btn-sm btn-danger" onClick={() => handleArrayRemove([...lPath, 'sections', 'resume', 'education', 'registers'], idx)}>Eliminar</button>
                  </div>
                  {renderInput('Título del Grado', [...lPath, 'sections', 'resume', 'education', 'registers', idx, 'title'])}
                  {renderInput('Período', [...lPath, 'sections', 'resume', 'education', 'registers', idx, 'period'])}
                  {renderInput('Lugar/Institución', [...lPath, 'sections', 'resume', 'education', 'registers', idx, 'place'])}
                  {renderInput('Descripción', [...lPath, 'sections', 'resume', 'education', 'registers', idx, 'description'], true)}
                </div>
              ))}
              <button className="btn btn-outline-primary btn-sm" onClick={() => handleArrayAdd([...lPath, 'sections', 'resume', 'education', 'registers'], { title: '', period: '', place: '', description: '' })}>+ Agregar Educación</button>

              <hr className="my-4"/>
              <h5>Experiencia</h5>
              {renderInput('Título (Experiencia)', [...lPath, 'sections', 'resume', 'experience', 'title'])}
              {(masterData[lang].sections?.resume?.experience?.registers || []).map((exp, idx) => (
                <div key={idx} className="card p-3 mb-3 border-1 border-primary shadow-sm">
                  <div className="d-flex justify-content-between mb-2">
                    <h6 className="m-0 text-primary">Experiencia {idx + 1}</h6>
                    <button className="btn btn-sm btn-danger" onClick={() => handleArrayRemove([...lPath, 'sections', 'resume', 'experience', 'registers'], idx)}>Eliminar Experiencia</button>
                  </div>
                  {renderInput('Empresa (Title)', [...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'title'])}
                  {renderInput('Rol', [...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'rol'])}
                  {renderInput('Período', [...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'period'])}
                  
                  <div className="mt-3">
                    <label className="fw-bold mb-2">Actividades:</label>
                    {(exp.activities || []).map((act, actIdx) => (
                      <div key={actIdx} className="d-flex mb-2 gap-2">
                        <textarea className="form-control" rows="2" value={act.value} onChange={(e) => handleInputChange([...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'activities', actIdx, 'value'], e.target.value)} />
                        <button className="btn btn-outline-danger" onClick={() => handleArrayRemove([...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'activities'], actIdx)}><i className="bi bi-x"></i></button>
                      </div>
                    ))}
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleArrayAdd([...lPath, 'sections', 'resume', 'experience', 'registers', idx, 'activities'], { value: '' })}>+ Actividad</button>
                  </div>
                </div>
              ))}
              <button className="btn btn-outline-primary btn-sm" onClick={() => handleArrayAdd([...lPath, 'sections', 'resume', 'experience', 'registers'], { title: '', rol: '', period: '', activities: [] })}>+ Agregar Experiencia</button>
            </div>
          </div>
        </div>

        {/* PROJECTS */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseProjects-${lang}`}>
              Sección: Proyectos (Portfolio)
            </button>
          </h2>
          <div id={`collapseProjects-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              {renderInput('Título de la Sección', [...lPath, 'sections', 'projects', 'sectionTitle'])}
              {renderInput('Descripción de la Sección', [...lPath, 'sections', 'projects', 'description'], true)}

              <hr className="my-4"/>
              <h5>Lista de Proyectos</h5>
              {(masterData[lang].sections?.projects?.registers || []).map((proj, idx) => (
                <div key={idx} className="card p-3 mb-4 shadow-sm border-info">
                  <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="m-0 text-info">Proyecto: {proj.title}</h5>
                    <button className="btn btn-sm btn-danger" onClick={() => handleArrayRemove([...lPath, 'sections', 'projects', 'registers'], idx)}>Eliminar Proyecto</button>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">{renderInput('Título Principal', [...lPath, 'sections', 'projects', 'registers', idx, 'title'])}</div>
                    <div className="col-md-6">{renderInput('Texto "Vista Previa"', [...lPath, 'sections', 'projects', 'registers', idx, 'previewText'])}</div>
                    <div className="col-md-6">{renderInput('Texto "Ver a Detalle"', [...lPath, 'sections', 'projects', 'registers', idx, 'detailText'])}</div>
                    <div className="col-md-6">{renderFileInput('Imagen Principal (src)', [...lPath, 'sections', 'projects', 'registers', idx, 'src'], 'portfolio')}</div>
                    <div className="col-md-12">{renderInput('Descripción Corta', [...lPath, 'sections', 'projects', 'registers', idx, 'description'], true)}</div>
                  </div>

                  <h6 className="mt-4 fw-bold">Detalle del Proyecto</h6>
                  <div className="bg-light p-3 rounded">
                    {renderInput('Título del Detalle', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'title'])}
                    
                    <div className="row mt-2">
                      <div className="col-md-4">{renderInput('Categoría (Label)', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'category', 'label'])}</div>
                      <div className="col-md-8">{renderInput('Categoría (Valor)', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'category', 'value'])}</div>
                      
                      <div className="col-md-4">{renderInput('Lenguaje (Label)', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'language', 'label'])}</div>
                      <div className="col-md-8">{renderInput('Lenguaje (Valor)', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'language', 'value'])}</div>
                    </div>
                    {renderInput('Descripción Larga', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'description', 'value'], true)}
                    {renderInput('URL Github (Opcional)', [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'projectInfo', 'git'])}

                    <hr className="my-3" />
                    <h6 className="fw-bold text-secondary">Imágenes del Proyecto (Galería)</h6>
                    {(proj.detail?.images || []).map((img, imgIdx) => (
                      <div key={imgIdx} className="d-flex mb-2 gap-2 align-items-center">
                        <span className="text-muted small">Img {imgIdx + 1}:</span>
                        <div className="input-group input-group-sm w-100">
                          <input type="text" className="form-control" placeholder="URL de la imagen" value={img.src || ''} onChange={(e) => handleInputChange([...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'images', imgIdx, 'src'], e.target.value)} />
                          <input type="file" id={`file_img_${idx}_${imgIdx}`} className="d-none" onChange={(e) => { handleFileUpload(e, [...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'images', imgIdx, 'src'], 'portfolio'); e.target.value = ''; }} />
                          <button className="btn btn-outline-primary" type="button" onClick={() => document.getElementById(`file_img_${idx}_${imgIdx}`).click()}>
                            <i className="bi bi-upload"></i>
                          </button>
                        </div>
                        <button className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => handleArrayRemove([...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'images'], imgIdx)} title="Eliminar Imagen">
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ))}
                    <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => handleArrayAdd([...lPath, 'sections', 'projects', 'registers', idx, 'detail', 'images'], { src: '' })}>
                      <i className="bi bi-plus"></i> Agregar Imagen a Galería
                    </button>
                  </div>
                </div>
              ))}
              <button className="btn btn-outline-info w-100" onClick={() => handleArrayAdd([...lPath, 'sections', 'projects', 'registers'], { title: 'Nuevo Proyecto', detail: { projectInfo: {} } })}>
                + Agregar Nuevo Proyecto
              </button>
            </div>
          </div>
        </div>

        {/* SKILLS */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseSkills-${lang}`}>
              Sección: Habilidades (Skills)
            </button>
          </h2>
          <div id={`collapseSkills-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              {renderInput('Título de la Sección', [...lPath, 'sections', 'skills', 'sectionTitle'])}
              {renderInput('Descripción', [...lPath, 'sections', 'skills', 'description'], true)}

              <hr className="my-4" />
              {['languages', 'db', 'tools', 'extra'].map(skillType => (
                <div key={skillType} className="mb-4">
                  <h6 className="fw-bold text-capitalize">Lista: {skillType}</h6>
                  <div className="row">
                    {(masterData[lang].sections?.skills?.[skillType] || []).map((sk, idx) => (
                      <div key={idx} className="col-md-6 mb-2">
                        <div className="input-group">
                          <span className="input-group-text w-25">Nombre</span>
                          <input type="text" className="form-control" value={sk.name} onChange={(e) => handleInputChange([...lPath, 'sections', 'skills', skillType, idx, 'name'], e.target.value)} />
                          <span className="input-group-text">%</span>
                          <input type="number" className="form-control" style={{maxWidth: '80px'}} value={sk.value} onChange={(e) => handleInputChange([...lPath, 'sections', 'skills', skillType, idx, 'value'], parseInt(e.target.value) || 0)} />
                          <button className="btn btn-danger" onClick={() => handleArrayRemove([...lPath, 'sections', 'skills', skillType], idx)}><i className="bi bi-x"></i></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => handleArrayAdd([...lPath, 'sections', 'skills', skillType], { name: '', value: 50 })}>+ Agregar a {skillType}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CONTACT */}
        <div className="accordion-item border-0 mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed rounded fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapseContact-${lang}`}>
              Sección: Contacto (Contact)
            </button>
          </h2>
          <div id={`collapseContact-${lang}`} className="accordion-collapse collapse" data-bs-parent={`#accordion-${lang}`}>
            <div className="accordion-body">
              <h6 className="fw-bold">Encabezado de la Sección</h6>
              <div className="row">
                <div className="col-md-12">{renderInput('Título', [...lPath, 'sections', 'contact', 'title'])}</div>
                <div className="col-md-12">{renderInput('Descripción', [...lPath, 'sections', 'contact', 'description'], true)}</div>
              </div>
              <hr />
              <h6 className="fw-bold">Textos del Formulario</h6>
              <div className="row">
                <div className="col-md-6">{renderInput('Label: Nombre', [...lPath, 'sections', 'contact', 'contactForm', 'name', 'label'])}</div>
                <div className="col-md-6">{renderInput('Label: Correo', [...lPath, 'sections', 'contact', 'contactForm', 'email', 'label'])}</div>
                <div className="col-md-6">{renderInput('Label: Asunto', [...lPath, 'sections', 'contact', 'contactForm', 'subject', 'label'])}</div>
                <div className="col-md-6">{renderInput('Label: Mensaje', [...lPath, 'sections', 'contact', 'contactForm', 'msg', 'label'])}</div>
                <div className="col-md-12">{renderInput('Mensaje Error Campos Requeridos', [...lPath, 'sections', 'contact', 'contactForm', 'error', 'label'])}</div>
              </div>
              <hr />
              <h6 className="fw-bold">Botón de Envío y Notificaciones</h6>
              <div className="row">
                <div className="col-md-6">{renderInput('Texto: Botón Enviar', [...lPath, 'sections', 'contact', 'contactForm', 'submit', 'label'])}</div>
                <div className="col-md-6">{renderInput('Texto: Botón Cargando', [...lPath, 'sections', 'contact', 'contactForm', 'submit', 'loading'])}</div>
                <div className="col-md-12">{renderInput('Mensaje: Éxito/Confirmación', [...lPath, 'sections', 'contact', 'contactForm', 'submit', 'succes'])}</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  };

  const renderMessages = () => {
    if (messages.length === 0) {
      return <div className="alert alert-info">No hay mensajes recibidos aún.</div>;
    }
    return messages.map(msg => (
      <div key={msg.id} className="message-card">
        <div className="message-header">
          <h6>{msg.name} ({msg.email})</h6>
          <small>{msg.date}</small>
        </div>
        <div className="message-subject mb-2">
          <strong>Asunto:</strong> {msg.subject}
        </div>
        <div className="message-body">
          {msg.message}
        </div>
      </div>
    ));
  };

  const renderVisits = () => {
    if (visitsList.length === 0) {
      return <div className="alert alert-info">No hay visitas registradas aún.</div>;
    }
    return visitsList.map((v, idx) => (
      <div key={v.id} className="card p-3 mb-3 shadow-sm border-0 border-start border-4 border-primary">
        <div className="d-flex justify-content-between mb-2">
          <h6 className="m-0 fw-bold">Visita Reciente #{idx + 1}</h6>
          <span className="badge bg-secondary">{v.register}</span>
        </div>
        <div className="text-muted small mb-2">ID: {v.id}</div>
        
        {v.events ? (
          <div className="mt-2">
            <strong>Historial de Eventos:</strong>
            <ul className="list-group list-group-flush mt-2">
              {Object.keys(v.events).map(evKey => (
                <li key={evKey} className="list-group-item py-1 px-0" style={{ fontSize: '0.9rem' }}>
                  <span className="text-primary me-2"><i className="bi bi-clock-history"></i> {v.events[evKey].date}</span>
                  {v.events[evKey].track}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-muted small mt-2">No se registraron eventos en esta visita.</div>
        )}
      </div>
    ));
  };

  if (authChecking) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: '#f4f6f9' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verificando sesión...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: '#f4f6f9' }}>
        <div className="card shadow-sm p-5 text-center" style={{ maxWidth: '400px' }}>
          <h3 className="mb-4 text-primary"><i className="bi bi-shield-lock"></i> Acceso Restringido</h3>
          <p className="text-muted mb-4">Inicia sesión con tu cuenta de administrador autorizada para acceder al panel.</p>
          <button className="btn btn-outline-dark d-flex align-items-center justify-content-center mx-auto mb-3" onClick={handleLogin}>
            <i className="bi bi-google me-2 text-danger"></i> Iniciar sesión con Google
          </button>
          {authError && <div className="alert alert-danger mt-3">{authError}</div>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: '#f4f6f9' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando datos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard container-fluid">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-3 col-lg-2">
          <div className="admin-sidebar shadow-sm d-flex flex-column h-100">
            <div>
              <h4>Admin Panel</h4>
              <ul className="admin-nav">
                <li>
                  <button className={activeTab === 'config' ? 'active' : ''} onClick={() => setActiveTab('config')}>
                    <i className="bi bi-gear me-2"></i> Configuración Gral.
                  </button>
                </li>
                <li>
                  <button className={activeTab === 'es' ? 'active' : ''} onClick={() => setActiveTab('es')}>
                    <i className="bi bi-translate me-2"></i> Español (ES)
                  </button>
                </li>
                <li>
                  <button className={activeTab === 'us' ? 'active' : ''} onClick={() => setActiveTab('us')}>
                    <i className="bi bi-translate me-2"></i> Inglés (US)
                  </button>
                </li>
                <li>
                  <button className={activeTab === 'messages' ? 'active' : ''} onClick={() => setActiveTab('messages')}>
                    <i className="bi bi-envelope me-2"></i> Mensajes
                    {messages.length > 0 && <span className="badge bg-primary ms-2">{messages.length}</span>}
                  </button>
                </li>
                <li>
                  <button className={activeTab === 'visits' ? 'active' : ''} onClick={() => setActiveTab('visits')}>
                    <i className="bi bi-graph-up me-2"></i> Analíticas
                  </button>
                </li>
              </ul>
            </div>
            
            <div className="mt-auto pt-4 border-top">
              <p className="text-muted small mb-2"><i className="bi bi-person-circle"></i> {user.email}</p>
              <button className="btn btn-sm btn-outline-danger w-100" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right"></i> Cerrar Sesión
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="col-md-9 col-lg-10 p-4">
          <div className="admin-content shadow-sm">
            {activeTab === 'config' && (
              <>
                <h3 className="mb-4 text-primary"><i className="bi bi-gear"></i> Configuración General</h3>
                {renderConfigSection()}
              </>
            )}
            
            {activeTab === 'es' && (
              <>
                <h3 className="mb-4 text-primary"><i className="bi bi-globe"></i> Contenido en Español</h3>
                {renderLanguageSection('es')}
              </>
            )}
            
            {activeTab === 'us' && (
              <>
                <h3 className="mb-4 text-primary"><i className="bi bi-globe"></i> Contenido en Inglés</h3>
                {renderLanguageSection('us')}
              </>
            )}

            {activeTab === 'messages' && (
              <>
                <h3 className="mb-4 text-primary"><i className="bi bi-envelope"></i> Bandeja de Mensajes</h3>
                {renderMessages()}
              </>
            )}

            {activeTab === 'visits' && (
              <>
                <h3 className="mb-4 text-primary"><i className="bi bi-graph-up"></i> Top 5 Visitas Más Recientes</h3>
                <p className="text-muted mb-4">Muestra el detalle del recorrido de los últimos visitantes en tu sitio.</p>
                {renderVisits()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Floating Save Button */}
      {(activeTab !== 'messages' && activeTab !== 'visits') && (
        <div className="save-btn-container">
          <button className="btn btn-primary btn-lg px-4 rounded-pill shadow" onClick={handleSave} disabled={saving}>
            {saving ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-save me-2"></i>
            )}
            {saving ? 'Guardando...' : 'Guardar Todos los Cambios'}
          </button>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast-container">
          <div className="toast show align-items-center text-white bg-success border-0 shadow-lg" role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body fs-6">
                <i className="bi bi-check-circle me-2"></i> {toastMessage}
              </div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToastMessage('')} aria-label="Close"></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
